import {
  API_PRESETS,
  ComfyTarget,
  HttpApiTarget,
  loadCredential,
  parseWorkflow,
  suggestBindings,
  type BodyMode,
  type HttpApiConfig,
  type RedesignTarget,
  type WorkflowBindings,
} from '@design-ai/redesign';

/**
 * Persisted redesign settings.
 *
 * Everything except the API key lives in `localStorage` as one blob; keys go
 * through `@design-ai/redesign`'s credential store so they are namespaced per
 * target and never end up in an export of these settings.
 */

export type TargetKind = 'comfy' | 'http';

export interface ComfySettings {
  baseUrl: string;
  authHeader: string;
  authPrefix: string;
  /** Raw API-format workflow JSON, as pasted. */
  workflowText: string;
  bindings: WorkflowBindings;
}

export interface HttpSettings {
  presetId: string;
  endpoint: string;
  authHeader: string;
  authPrefix: string;
  bodyMode: BodyMode;
  fields: NonNullable<HttpApiConfig['fields']>;
  /** Constant body fields as JSON, e.g. `{"model":"gpt-image-1"}`. */
  extraText: string;
  responsePath: string;
  responseEncoding: 'base64' | 'url';
}

export interface RedesignConfig {
  targetKind: TargetKind;
  prompt: string;
  negativePrompt: string;
  /** Trim transparent margin before sending. */
  trim: boolean;
  /** 0 sends full size. Generative backends resample anyway, so this is mostly transfer size. */
  maxEdge: number;
  comfy: ComfySettings;
  http: HttpSettings;
}

const STORAGE_KEY = 'design-ai:redesign-config';

export const DEFAULT_CONFIG: RedesignConfig = {
  targetKind: 'comfy',
  prompt: '',
  negativePrompt: '',
  trim: true,
  maxEdge: 1536,
  comfy: {
    baseUrl: 'http://127.0.0.1:8188',
    authHeader: 'Authorization',
    authPrefix: 'Bearer ',
    workflowText: '',
    bindings: {},
  },
  http: {
    presetId: 'openai-images-edit',
    endpoint: '',
    authHeader: 'Authorization',
    authPrefix: 'Bearer ',
    bodyMode: 'multipart',
    fields: { image: 'image', mask: 'mask', prompt: 'prompt' },
    extraText: '{\n  "model": "gpt-image-1"\n}',
    responsePath: 'data[].b64_json',
    responseEncoding: 'base64',
  },
};

export function loadConfig(): RedesignConfig {
  if (typeof localStorage === 'undefined') return structuredClone(DEFAULT_CONFIG);
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return structuredClone(DEFAULT_CONFIG);
    const parsed = JSON.parse(stored) as Partial<RedesignConfig>;
    // Merge rather than replace, so a config saved by an older build still loads.
    return {
      ...structuredClone(DEFAULT_CONFIG),
      ...parsed,
      comfy: { ...DEFAULT_CONFIG.comfy, ...parsed.comfy },
      http: { ...DEFAULT_CONFIG.http, ...parsed.http },
    };
  } catch {
    return structuredClone(DEFAULT_CONFIG);
  }
}

export function saveConfig(config: RedesignConfig): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

/** Applies a preset's shape while keeping the endpoint the user typed. */
export function applyPreset(settings: HttpSettings, presetId: string): HttpSettings {
  const preset = API_PRESETS[presetId];
  if (!preset) return { ...settings, presetId };
  return {
    ...settings,
    presetId,
    bodyMode: preset.bodyMode,
    fields: { ...preset.fields },
    extraText: preset.extra ? JSON.stringify(preset.extra, null, 2) : '{}',
    responsePath: preset.responsePath ?? '',
    responseEncoding: preset.responseEncoding ?? 'base64',
  };
}

/**
 * Turns settings into a live target.
 *
 * Throws with a readable message on bad input (unparseable workflow, missing
 * endpoint) so the panel can show it before anything is sent.
 */
export function buildTarget(config: RedesignConfig): RedesignTarget {
  if (config.targetKind === 'comfy') {
    const { comfy } = config;
    if (!comfy.baseUrl.trim()) throw new Error('Set the ComfyUI server URL.');
    if (!comfy.workflowText.trim()) throw new Error('Paste a workflow exported in API format.');

    const workflow = parseWorkflow(comfy.workflowText);
    const bindings =
      Object.keys(comfy.bindings).length > 0 ? comfy.bindings : suggestBindings(workflow);

    return new ComfyTarget({
      baseUrl: comfy.baseUrl.trim(),
      apiKey: loadCredential('comfyui'),
      authHeader: comfy.authHeader,
      authPrefix: comfy.authPrefix,
      workflow,
      bindings,
    });
  }

  const { http } = config;
  if (!http.endpoint.trim()) throw new Error('Set the API endpoint.');

  let extra: Record<string, string | number | boolean> = {};
  if (http.extraText.trim()) {
    try {
      extra = JSON.parse(http.extraText) as Record<string, string | number | boolean>;
    } catch (cause) {
      throw new Error(`Extra fields are not valid JSON: ${cause instanceof Error ? cause.message : cause}`);
    }
  }

  return new HttpApiTarget({
    id: 'http-api',
    label: API_PRESETS[http.presetId]?.label ?? 'Image API',
    endpoint: http.endpoint.trim(),
    apiKey: loadCredential('http-api'),
    authHeader: http.authHeader,
    authPrefix: http.authPrefix,
    bodyMode: http.bodyMode,
    fields: http.fields,
    extra,
    responsePath: http.responsePath || undefined,
    responseEncoding: http.responseEncoding,
  });
}
